import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import Widget from '../../../components/widget/widget.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ActionButton from '../../../components/action-button/action-button.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import RemoveIcon from '../../../icons/remove-icon';
import StructureService from '../../../services/structure-service';
import FormService from '../../../services/form-service';
import {
  DEAL_TOP_LEVEL_METRICS_CREATE, DEAL_TOP_LEVEL_METRICS_DELETE, DEAL_TOP_LEVEL_METRICS_EDIT,
} from '../../../constants/entity/permissions';

const DealTopLevelMetrics: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Top Level Metrics';
  const WIDGET_NAME: string = 'widget-deal-top-level-metrics';
  const COLUMNS_TOTAL: number = 2;
  const ITEMS_VISIBLE_LIMIT: number = 7;

  const {
    isEditMode,
    isNewDeal,
    dealTopLevelMetricsData,
    dealTopLevelMetricsNewItems,
    onAddNewItem,
    onDeleteClick,
    onEditModeButtonClick,
  } = props;

  const [dealTopLevelMetricsItems, setDealTopLevelMetricsItems]
    : [Record<string, DealTopLevelMetricsItem> | null, Function] = useState(null);

  const dealTopLevelMetricsEditPermissions: string[] = useMemo((): string[] => [DEAL_TOP_LEVEL_METRICS_EDIT], []);
  const dealTopLevelMetricsCreatePermissions: string[] = useMemo((): string[] => [DEAL_TOP_LEVEL_METRICS_CREATE], []);
  const dealTopLevelMetricsDeletePermissions: string[] = useMemo((): string[] => [DEAL_TOP_LEVEL_METRICS_DELETE], []);

  const formPrefix: string = 'dealData.dealTopLevelMetricsData';
  const isEmpty: boolean = !isEditMode && !isNewDeal && (!dealTopLevelMetricsData || !dealTopLevelMetricsData.length);
  const dealTopLevelMetricsItemValues: DealTopLevelMetricsItem[] = (dealTopLevelMetricsItems
    && Object.values(dealTopLevelMetricsItems)) || [];
  const dealTopLevelMetricsTotalItems: number = (dealTopLevelMetricsData && dealTopLevelMetricsData.length) || 0;

  const addNewItem: MouseEventHandlerFunction = useCallback((): void => {
    if (onAddNewItem) {
      onAddNewItem({
        id: StructureService.getNewItemId(dealTopLevelMetricsNewItems, dealTopLevelMetricsData),
        name: '',
        value: '',
      });
    }
  }, [dealTopLevelMetricsData, dealTopLevelMetricsNewItems]);

  const deleteClick: Function = useCallback((dealTopLevelMetricsItemId: number): void => {
    if (onDeleteClick) {
      onDeleteClick(dealTopLevelMetricsItemId);
    }

    if (dealTopLevelMetricsItems) {
      const newDealTopLevelMetricsItems: Record<string, DealTopLevelMetricsItem> = dealTopLevelMetricsItems;
      delete newDealTopLevelMetricsItems[dealTopLevelMetricsItemId];
      setDealTopLevelMetricsItems({ ...newDealTopLevelMetricsItems });
    }
  }, [dealTopLevelMetricsItems, dealTopLevelMetricsNewItems]);

  useEffect((): void => {
    if (dealTopLevelMetricsData) {
      const initialDealTopLevelMetricsItems: Record<string, DealTopLevelMetricsItem> = {};
      dealTopLevelMetricsData.forEach((dealTopLevelMetricsItem: DealTopLevelMetricsItem): void => {
        initialDealTopLevelMetricsItems[dealTopLevelMetricsItem.id] = { ...dealTopLevelMetricsItem };
      });
      setDealTopLevelMetricsItems(initialDealTopLevelMetricsItems);
    }
  }, [dealTopLevelMetricsData, isEditMode]);

  return (
    <CheckAccess permissions={dealTopLevelMetricsEditPermissions} isEnabled={isEditMode || isNewDeal}>
      <Widget
        isScrollable={isEditMode || isNewDeal || dealTopLevelMetricsTotalItems > ITEMS_VISIBLE_LIMIT}
        isEmpty={isEmpty}
        isEditMode={isEditMode}
        withEditMode={!isNewDeal}
        editPermissions={dealTopLevelMetricsEditPermissions}
        widgetName={WIDGET_NAME}
        defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
        onEditModeButtonClick={onEditModeButtonClick}
      >
        <Table>
          <TableBody isPlain withZebraRows withHeaderColumn>
            {
              [
                ...dealTopLevelMetricsItemValues,
                ...(dealTopLevelMetricsNewItems.filter((dealTopLevelMetricsItem: DealTopLevelMetricsItem): boolean => (
                  !dealTopLevelMetricsItemValues.find((
                    dealTopLevelMetricsItemValue: DealTopLevelMetricsItem,
                  ): boolean => (
                    dealTopLevelMetricsItemValue.id === dealTopLevelMetricsItem.id
                  ))
                )) || []),
              ].map((dealTopLevelMetricsItem: DealTopLevelMetricsItem): ReactElement => {
                const isNewItem: boolean = dealTopLevelMetricsNewItems
                  && !!dealTopLevelMetricsNewItems.length
                  && !!dealTopLevelMetricsNewItems.find((
                    dealTopLevelMetricsNewItem: DealTopLevelMetricsItem,
                  ): boolean => (
                    dealTopLevelMetricsNewItem.id === dealTopLevelMetricsItem.id
                  ));
                const formKeyDealTopLevelMetricsItem: string = FormService.getIndexedFormFieldKey(
                  dealTopLevelMetricsItem.id,
                );

                return (
                  <TableRow isActive={isNewItem && isEditMode && !isNewDeal} key={dealTopLevelMetricsItem.id}>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <Fragment>
                            <FormFieldText
                              isHidden
                              name={`${formPrefix}[${formKeyDealTopLevelMetricsItem}].id`}
                            />
                            <FormFieldText
                              name={`${formPrefix}[${formKeyDealTopLevelMetricsItem}].name`}
                              placeholder="Name"
                            />
                          </Fragment>
                        ) : dealTopLevelMetricsItem.name
                      }
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FlexContainer>
                            <FormFieldText
                              name={`${formPrefix}[${formKeyDealTopLevelMetricsItem}].value`}
                              placeholder="Value"
                            />
                            <CheckAccess permissions={dealTopLevelMetricsDeletePermissions}>
                              <div>
                                <Tooltip title="Delete">
                                  <ActionButton
                                    isInline
                                    withNoPadding
                                    onClick={(): void => {
                                      deleteClick(dealTopLevelMetricsItem.id);
                                    }}
                                  >
                                    <RemoveIcon />
                                  </ActionButton>
                                </Tooltip>
                              </div>
                            </CheckAccess>
                          </FlexContainer>
                        ) : dealTopLevelMetricsItem.value
                      }
                    </TableCell>
                  </TableRow>
                );
              })
            }
            {
              (isEditMode || isNewDeal) && (
                <CheckAccess permissions={dealTopLevelMetricsCreatePermissions}>
                  <TableRowAddNewItem colSpan={COLUMNS_TOTAL} onClick={addNewItem} />
                </CheckAccess>
              )
            }
          </TableBody>
        </Table>
      </Widget>
    </CheckAccess>
  );
});

export default DealTopLevelMetrics;
